import { cn } from "@lib/cn";
import { IconColorPicker } from "@tabler/icons-react";
import debounce from "lodash.debounce";
import {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";
import {
	RgbaColorPicker as ReactColorful,
	type RgbaColor,
} from "react-colorful";
import Input from "../ui/input";
import { hexToRgba, normalizeHexColor, rgbaToHex } from "./lib/rgba";

export interface SolidEditorProps {
	current?: string;
	onColorChange?: (color: string) => void;
	onColorAdd?: (color: string) => void;
	onGoBack?: () => void;
	className?: string;
	isEditing?: boolean;
}

export const MemoizedColorPicker = memo(
	({
		color,
		onChange,
	}: {
		color: RgbaColor;
		onChange: (color: RgbaColor) => void;
	}) => {
		return <ReactColorful color={color} onChange={onChange} />;
	},
);
MemoizedColorPicker.displayName = "MemoizedColorPicker";

const useColorState = (
	initialHex: string,
	onChange?: (hex: string) => void,
) => {
	const [rgbaColor, setRgbaColor] = useState<RgbaColor>(() =>
		hexToRgba(initialHex),
	);
	const [hexColor, setHexColor] = useState(initialHex);

	const debouncedHexUpdate = useMemo(
		() =>
			debounce((rgba: RgbaColor) => {
				const hex = rgbaToHex(rgba);
				setHexColor(hex);
				onChange?.(hex);
			}, 250),
		[onChange],
	);

	useEffect(() => {
		return () => {
			debouncedHexUpdate.cancel();
		};
	}, [debouncedHexUpdate]);

	const updateRgbaColor = useCallback(
		(rgba: RgbaColor) => {
			setRgbaColor(rgba);
			debouncedHexUpdate(rgba);
		},
		[debouncedHexUpdate],
	);

	const updateHexColor = useCallback(
		(hex: string) => {
			const normalized = normalizeHexColor(hex);
			setHexColor(normalized);
			setRgbaColor(hexToRgba(normalized));
			onChange?.(normalized);
		},
		[onChange],
	);

	return { rgbaColor, hexColor, updateRgbaColor, updateHexColor };
};

const SolidEditor = ({
	current,
	onColorChange,
	onColorAdd,
	onGoBack,
	className,
	isEditing,
}: SolidEditorProps) => {
	const { rgbaColor, hexColor, updateRgbaColor, updateHexColor } =
		useColorState(current || "#FFFFFF", onColorChange);

	const [isEyeDropperSupported, setIsEyeDropperSupported] = useState(false);
	const [isEyeDropperActive, setIsEyeDropperActive] = useState(false);
	const [inputValue, setInputValue] = useState(hexColor);
	const [isInputFocused, setIsInputFocused] = useState(false);

	useLayoutEffect(() => {
		setIsEyeDropperSupported("EyeDropper" in window);
	}, []);

	const handleEyeDropper = useCallback(async () => {
		if (!isEyeDropperSupported) return;

		try {
			setIsEyeDropperActive(true);
			const eyeDropper = new EyeDropper();
			const result = await eyeDropper.open();
			const newColor = result.sRGBHex.toUpperCase();
			updateHexColor(newColor);
		} catch {
			// Silently handle user cancellation
		}
		setIsEyeDropperActive(false);
	}, [isEyeDropperSupported, updateHexColor]);

	const handleColorAdd = useCallback(
		(hex: string) => {
			onColorAdd?.(hex);
		},
		[onColorAdd],
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setInputValue(e.target.value);
			updateHexColor(e.target.value);
		},
		[updateHexColor],
	);

	useEffect(() => {
		if (!isInputFocused) {
			setInputValue(rgbaToHex(rgbaColor));
		}
	}, [isInputFocused, rgbaColor]);

	return (
		<div
			className={cn("tw-w-full tw-p-4", className)}
			data-testid="solid-editor"
		>
			<div className="tw-flex tw-flex-col tw-gap-4" id="color-picker">
				{/* Color picker */}
				<div className="tw-relative">
					<MemoizedColorPicker color={rgbaColor} onChange={updateRgbaColor} />
					<span
						className="tw-absolute -tw-bottom-1 tw-left-0 tw-size-9 tw-rounded-full tw-border tw-border-solid tw-border-blue-100 tw-bg-white"
						style={{
							backgroundColor: `rgba(${rgbaColor.r}, ${rgbaColor.g}, ${rgbaColor.b}, ${rgbaColor.a})`,
						}}
					/>
				</div>

				{/* Color input */}
				<div className="tw-flex tw-items-center [&>div:first-child]:tw-flex-grow">
					<Input
						onFocus={() => setIsInputFocused(true)}
						onBlur={() => setIsInputFocused(false)}
						value={inputValue}
						placeholder="#FFFFFF"
						className="tw-h-9 tw-px-3 tw-py-1 tw-uppercase"
						onChange={handleInputChange}
					/>
					{isEyeDropperSupported && (
						<button
							type="button"
							onClick={handleEyeDropper}
							className={cn(
								"tw-ml-2 tw-flex tw-size-9 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-white tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue-base/5 hover:tw-bg-blue-50",
								isEyeDropperActive && "tw-bg-blue-200 tw-outline-blue-300",
							)}
						>
							<IconColorPicker className="tw-size-5" />
						</button>
					)}
				</div>

				{/* Color picker actions */}
				{(onColorAdd || onGoBack) && (
					<div className="tw-flex tw-flex-col tw-gap-2">
						{onColorAdd && (
							<button
								type="button"
								disabled={
									!hexColor ||
									!/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(
										hexColor,
									)
								}
								onClick={() => handleColorAdd(hexColor)}
								className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-primary-base tw-font-sans tw-text-sm tw-font-medium tw-text-white tw-shadow-sm tw-transition-colors hover:tw-opacity-90 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
							>
								{isEditing ? "Save changes" : "Use color"}
							</button>
						)}
						{onGoBack && (
							<button
								type="button"
								onClick={onGoBack}
								className="tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-white tw-font-sans tw-text-sm tw-font-medium tw-text-[#12033E] tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue-base/5 tw-transition-colors hover:tw-bg-blue-50"
							>
								Go back
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default SolidEditor;
